import { ReactComponent as Memo } from "../../../../assets/icons/memo.svg";
import { ReactComponent as MemoOff } from "../../../../assets/icons/memoOff.svg";
import { ReactComponent as LeftArrow } from "../../../../assets/icons/leftArrow.svg";
import { ReactComponent as RightArrow } from "../../../../assets/icons/rightArrow.svg";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/closeIcon.svg";

import Button from "../../../../../common/components/button";
import Input from "../../../../components/common/input";
import DayPickerInput from "../../../../components/common/DayPickerInput";
import SelectBox from "../../../../components/common/selectBox";
import React, { useEffect, useState } from "react";
import { useGetSalesPayment } from "../../../../../common/hooks/console/sales/useGetSalesPayment";
import { usePutSalesMemo } from "../../../../../common/hooks/console/sales/usePutSalesMemo";
import useToastStore from "../../../../store/useToastStore";
import { useGetCommonPayMethod } from "../../../../../common/hooks/common/useGetCommonPayMethod";
import { usePostPaymentSales } from "../../../../../common/hooks/console/sales/usePostSales";
import { useSystemPopStore } from "../../../../store/useSystemPopStore";
import { useAuthStore } from "../../../../store/useAuthStore";
import { useGetSalesDeliveryDate } from "../../../../../common/hooks/console/sales/useGetDeliveryDate";

interface SalesInfoProps {
    pendingAmount?: number;
    customerIdx: number;
    orderIdx?: number;
    customer?: string;
    oldestDeliveryDate: string;
    latestDeliveryDate: string;
    startDate?: Date;
    endDate?: Date;
    onDateChange?: (start: Date | undefined, end: Date | undefined) => void;
    selectedOrders?: number[];
    search?: string;
    searchTxt?: string;
}

const SalesInfo = ({
    pendingAmount,
    customerIdx,
    orderIdx,
    customer,
    oldestDeliveryDate,
    latestDeliveryDate,
    startDate,
    endDate,
    onDateChange,
    selectedOrders,
    search,
    searchTxt,
}: SalesInfoProps) => {
    const { addToast } = useToastStore();
    const { showPop } = useSystemPopStore();
    const level = useAuthStore(state => state.m_level);
    const name = useAuthStore(state => state.m_name);

    const [openMemoIdx, setOpenMemoIdx] = useState<number | null>(null);
    const [memo, setMemo] = useState<string>("");
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [inputValue, setInputValue] = useState<string>(currentPage?.toString() || "");
    const [method, setMethod] = useState<string>("");
    const [methodValue, setMethodValue] = useState<string>("");
    const [payAmount, setPayAmount] = useState<string>("");
    const [memoInput, setMemoInput] = useState<string>("");
    const [payDate, setPayDate] = useState<Date | undefined>(new Date());
    const [payDateError, setPayDateError] = useState<boolean>(false);
    const [methodError, setMethodError] = useState<boolean>(false);
    const [payAmountError, setPayAmountError] = useState<boolean>(false);

    const { data: deliveryDateData } = useGetSalesDeliveryDate({ customerIdx });
    const { data: paymentData } = useGetSalesPayment({
        customerIdx: customerIdx,
        order_idx: orderIdx,
        page: currentPage,
    });

    const { data: methodData } = useGetCommonPayMethod({ payment_category: 1 });

    const { mutate: memoModifyMutate } = usePutSalesMemo(() => {
        setOpenMemoIdx(null);
        setMemo("");
        addToast({
            type: "success",
            message: "메모가 수정되었습니다.",
        });
    });
    const { mutate: postSalesMutate } = usePostPaymentSales(customerIdx, customer === "개인 회원" ? orderIdx : undefined, () => {
        addToast({
            type: "success",
            message: "입금 내역을 등록하었습니다.",
        });
    });

    const handleSubmit = () => {
        let hasError = false;
        if (!payDate) {
            setPayDateError(true);
            hasError = true;
        }
        if (!method.trim()) {
            setMethodError(true);
            hasError = true;
        }
        if (!payAmount.trim()) {
            setPayAmountError(true);
            hasError = true;
        }
        if (hasError) return;

        const hasSelectedOrders = (selectedOrders?.length ?? 0) > 0;
        const trimmedSearchTxt = searchTxt?.trim() ?? "";

        postSalesMutate(
            {
                payment_date: payDate?.toLocaleDateString("sv-SE") ?? "",
                payment_method: method,
                payment_amount: Number(payAmount.replace(/,/g, "")),
                payment_memo: memoInput,
                start_date: startDate?.toISOString().split("T")[0] ?? "",
                end_date: endDate?.toISOString().split("T")[0] ?? "",
                ...(hasSelectedOrders ? { order_idx_list: selectedOrders } : {}),
                ...(!hasSelectedOrders && trimmedSearchTxt ? { search, searchtxt: trimmedSearchTxt } : {}),
            },
            {
                onSuccess: data => {
                    setPayDate(undefined);
                    setMethod("");
                    setMethodValue("");
                    setPayAmount("");
                    setMemoInput("");
                    setPayDate(new Date());
                },
                onError: (error: any) => {
                    const errorMessage = error?.response?.data?.message || "알 수 없는 오류가 발생했습니다.";
                    showPop({
                        title: errorMessage,
                        rightButton: undefined,
                        type: "negative",
                        onRightClick: () => {},
                    });
                },
            },
        );
    };

    const handleMemoModify = (customerId: number, paymentHistoryIdx: number) => {
        memoModifyMutate({
            customerId,
            paymentHistoryIdx,
            customerMemoData: { payment_memo: memo },
        });
    };

    const handleStartDateChange = (dateValue: Date | undefined) => {
        if (!dateValue) return;
        const adjustedDate = new Date(dateValue);
        adjustedDate.setMinutes(adjustedDate.getMinutes() - adjustedDate.getTimezoneOffset());

        onDateChange?.(adjustedDate, endDate);
    };

    const handleEndDateChange = (dateValue: Date | undefined) => {
        if (!dateValue) return;
        const adjustedDate = new Date(dateValue);
        adjustedDate.setMinutes(adjustedDate.getMinutes() - adjustedDate.getTimezoneOffset());

        onDateChange?.(startDate, adjustedDate);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        setInputValue(page.toString());
    };

    const handlePageInput = (value: string) => {
        if (value.trim() === "") {
            setInputValue("");
            return;
        }
        if (!/^\d*$/.test(value)) {
            return;
        }
        const pageNumber = Number(value.trim());
        if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > (paymentData?.last_page || 1)) {
            alert("페이지 범위를 벗어났습니다.");
            setInputValue(value.slice(0, -1));
            return;
        }

        setInputValue(value);
        handlePageChange(pageNumber);
    };

    const handleChange = (e: any) => {
        let rawValue = e.target.value.replace(/[^0-9]/g, "");

        if (rawValue === "") {
            setPayAmount("");
            return;
        }

        const numericValue = Number(rawValue);
        setPayAmount(numericValue.toLocaleString());
    };
    useEffect(() => {
        if (!startDate && !endDate && (deliveryDateData?.oldest_delivery_date || deliveryDateData?.latest_delivery_date)) {
            const start = deliveryDateData?.oldest_delivery_date ? new Date(deliveryDateData.oldest_delivery_date) : undefined;
            const end = deliveryDateData?.latest_delivery_date ? new Date(deliveryDateData.latest_delivery_date) : undefined;
            onDateChange?.(start, end);
        }
    }, [deliveryDateData]);

    const normalizeMemo = (text?: string) => {
        if (!text) return "";
        return text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    };

    return (
        <div className="bg-white pt-5 h-full overflow-y-auto rounded-xl border border-gray4 shadow-custom flex flex-col gap-4">
            <div className="flex px-3 flex-col gap-2">
                <p className="text-xl font-bold">일괄 입금등록</p>
                <div className="bg-gray6 p-3 rounded-lg ">
                    <div className="flex gap-3">
                        <div className="w-full flex flex-col gap-2 ">
                            {customer === "개인 회원" || (
                                <div className="bg-white py-1.5 px-3 rounded-lg flex items-center justify-between">
                                    <p className="flex-shrink-0 text-sm">대상 주문 기간조회</p>
                                    <div className="flex gap-2 items-center -translate-y-[1px] relative z-50">
                                        <DayPickerInput
                                            disabled={level === 7}
                                            initialDate={startDate}
                                            onDateChange={handleStartDateChange}
                                            className="h-full z-10 rounded-lg max-w-[110px]"
                                        />
                                        <p className="text-sm text-gray2">~</p>
                                        <DayPickerInput
                                            disabled={level === 7}
                                            initialDate={endDate}
                                            onDateChange={handleEndDateChange}
                                            className="h-full rounded-lg max-w-[110px]"
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-1">
                                <div
                                    className={`${payDateError && "border-error border"} bg-white py-1.5 px-3 rounded-lg w-full flex items-center justify-between`}
                                >
                                    <p className="text-sm">입금일자</p>
                                    <DayPickerInput
                                        disabled={level === 7 ? true : false}
                                        right
                                        initialDate={payDate}
                                        onDateChange={date => {
                                            setPayDateError(false);
                                            setPayDate(date);
                                        }}
                                        className="h-full rounded-lg max-w-[110px]"
                                    />
                                </div>
                                <div
                                    className={`bg-white py-1.5 px-3 rounded-lg w-full border ${methodError ? "border-error" : "border-gray4"} flex items-center gap-2`}
                                >
                                    <p className="text-sm flex-shrink-0">결제수단</p>
                                    <SelectBox
                                        disabled={level === 7}
                                        status="view"
                                        className="w-full"
                                        options={
                                            methodData?.data.map(method => ({
                                                value: method.payment_order.toString(),
                                                label: method.payment_name,
                                            })) || []
                                        }
                                        value={methodValue}
                                        onChange={value => {
                                            const selectedMethod = methodData?.data.find(
                                                method => method.payment_order.toString() === value,
                                            );
                                            setMethodValue(value);
                                            setMethod(selectedMethod?.payment_name || "");
                                            setMethodError(false);
                                        }}
                                    />
                                </div>
                            </div>
                            <div
                                className={`bg-white w-full items-center text-sm pl-3 justify-between ${payAmountError ? "border-error" : "border-gray4"} rounded-lg flex border`}
                            >
                                <p className="whitespace-nowrap">입금액</p>
                                <Input
                                    inputClassName="border-none pb-[4.5px]"
                                    value={payAmount}
                                    onChange={handleChange}
                                    price
                                    className={`font-bold px-3 w-full`}
                                    status={level === 7 ? "view" : undefined}
                                />
                            </div>
                            <div className="py-1.5 text-sm px-3 rounded-lg flex w-full justify-between bg-gray5 border border-gray4 text-error">
                                <p>미수금액</p>
                                <p>{pendingAmount?.toLocaleString()}원</p>
                            </div>
                            <Input
                                status={level === 7 ? "view" : undefined}
                                value={memoInput}
                                onChange={e => setMemoInput(e.target.value)}
                                placeholder="메모입력(100자)"
                                maxLength={100}
                            />
                        </div>
                        {level !== 7 && (
                            <Button onClick={handleSubmit} size="S" status="regist" className="px-3 min-h-full flex items-center truncate">
                                입금 등록
                            </Button>
                        )}
                    </div>
                </div>
            </div>
            <div>
                <div className="flex px-3 items-center justify-between h-[58px]">
                    <p className="text-xl">입금 등록 내역</p>
                    <div className="flex items-center gap-2">
                        <button onClick={() => currentPage && currentPage > 1 && handlePageChange(currentPage - 1)}>
                            <LeftArrow />
                        </button>
                        <Input
                            inputClassName="text-center"
                            className="w-20 text-center outline-0 text-sm flex"
                            value={paymentData?.last_page ? inputValue : "0"}
                            onChange={e => handlePageInput(e.target.value)}
                        />
                        <p className="text-gray3">/ {paymentData?.last_page || 0}</p>
                        <button
                            onClick={() => currentPage && currentPage < (paymentData?.last_page || 1) && handlePageChange(currentPage + 1)}
                        >
                            <RightArrow />
                        </button>
                    </div>
                </div>
                <table className="text-sm text-gray2 w-full border-t border-gray3">
                    <thead className="h-10">
                        <tr>
                            <th className="min-w-[110px] px-2 py-3 w-1/4 text-start">입금일자</th>
                            <th className="min-w-[110px] px-2 py-3 w-1/4 text-start">등록일자</th>
                            <th className="min-w-[90px] px-2 py-3 text-start">결제수단</th>
                            <th className="min-w-[110px] px-2 py-3 w-1/4 text-start">입금액</th>
                            <th className="min-w-[85px] px-2 py-3 text-start">담당자</th>
                            <th className="min-w-[52px] px-2 py-3 text-center">메모</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paymentData?.data_list && paymentData?.data_list.length > 0 ? (
                            paymentData?.data_list.map((item, index) => (
                                <tr key={`${item.order_idx}-${index}`} className="h-[50px] border-y border-gray4 hover:bg-primary4">
                                    <td className="px-2">{item.payment_date.replaceAll("-", ".")}</td>
                                    <td className="px-2">{item.reg_date ? item.reg_date.replaceAll("-", ".") : "-"}</td>
                                    <td className={`px-2 ${item.payment_amount < 0 ? "text-error" : ""}`}>
                                        {item.payment_method || "입금취소"}
                                    </td>
                                    <td className="px-2 text-end bg-primary4 whitespace-nowrap">{item.payment_amount.toLocaleString()}원</td>
                                    <td className="px-2">{item.manager_name}</td>
                                    <td className="px-2 text-center mx-auto relative">
                                        <div
                                            onClick={() => {
                                                setOpenMemoIdx(item.payment_history_idx);

                                                // 최초 오픈일 때만 서버 메모 로드
                                                setMemo(prev => {
                                                    if (prev) return prev;
                                                    return normalizeMemo(item.payment_memo);
                                                });
                                                // setMemo(String(item.payment_memo || ""));
                                            }}
                                            className="flex justify-center cursor-pointer"
                                        >
                                            {item.payment_memo ? <Memo /> : <MemoOff />}
                                        </div>
                                        {openMemoIdx === item.payment_history_idx && (
                                            <div className="bg-white rounded-2xl z-10  shadow-custom w-[420px] top-10 absolute right-2">
                                                <div className="flex items-center justify-between px-6 py-5">
                                                    <p className="text-2xl font-bold text-[#1C1C1E]">메모</p>
                                                    <CloseIcon
                                                        className="cursor-pointer"
                                                        onClick={() => {
                                                            setOpenMemoIdx(null);
                                                            setMemo("");
                                                        }}
                                                    />
                                                </div>
                                                <div className="flex flex-col px-6 pb-5">
                                                    <textarea
                                                        maxLength={100}
                                                        disabled={level === 7 || (level === 8 && item.manager_name !== name)}
                                                        value={memo}
                                                        onChange={e => setMemo(String(e.target.value))}
                                                        className="h-[120px] border border-gray disabled:mb-3 text-sm px-3 outline-0 rounded-lg py-2 resize-none"
                                                        placeholder="메모 입력"
                                                    />
                                                    <span
                                                        className={`text-sm m-[8px_0_20px_auto] ${memo.length === 100 ? "text-error" : "text-gray3 "}`}
                                                    >
                                                        {memo.length ?? 0}/100
                                                    </span>
                                                    {level !== 7 && (level !== 8 || item.manager_name === name) && (
                                                        <Button
                                                            size="S"
                                                            className="w-[68px] ml-auto"
                                                            onClick={() => handleMemoModify(item.customer_idx, item.payment_history_idx)}
                                                        >
                                                            메모 수정
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={6} className="text-center pt-10 text-grayicon">
                                    등록된 내역이 없습니다.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default SalesInfo;
